import Header from "./Header";
import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { CircularProgressbar } from "react-circular-progressbar";
import "./styles/Reports.css";

const API_BASE = "https://backend.training.racetrackpitstop.co.uk";

function average(list) {
  if (!list || list.length === 0) return "0";
  const total = list.reduce(
    (sum, item) => sum + (parseInt(item.percent, 10) || 0),
    0,
  );
  return (total / list.length).toFixed();
}

export default function Reports() {
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState(null);

  const [site, setSite] = useState("");
  const [showSites, setShowSites] = useState(false);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteError, setSiteError] = useState(null);

  const [employeesData, setEmployeesData] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [csas, setCsas] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managerAv, setManagerAv] = useState("0");
  const [superAv, setSuperAv] = useState("0");
  const [csaAv, setCsaAv] = useState("0");
  const [emails, setEmails] = useState(0);
  const [newStaff, setNewStaff] = useState({});
  const [oldStaff, setOldStaff] = useState({});
  const [staffLogged, setStaffLogged] = useState({});
  const [parentTasks, setParentTasks] = useState({});
  const [childTasks, setChildTasks] = useState({});
  const [parentTasksAllTM, setParentTasksAllTM] = useState([]);
  const [parentTasksAllLM, setParentTasksAllLM] = useState([]);
  const [childTasksAllTM, setChildTasksAllTM] = useState([]);
  const [childTasksAllLM, setChildTasksAllLM] = useState([]);

  const [removingName, setRemovingName] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadSites() {
      setSitesLoading(true);
      setSitesError(null);
      try {
        const user = localStorage.getItem("user");
        const res = await axios.get(
          `${API_BASE}/trainingAdmin/reporting/${user}`,
        );
        if (!cancelled) setSites(res.data);
      } catch (err) {
        console.log(err);
        if (!cancelled) setSitesError("Couldn't load sites. Please try again.");
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    }

    loadSites();
    return () => {
      cancelled = true;
    };
  }, []);

  const showSiteTraining = useCallback(async (selectedSite) => {
    setSite(selectedSite);
    setSiteLoading(true);
    setSiteError(null);

    try {
      const [
        siteRes,
        newStaffRes,
        oldStaffRes,
        staffLoggedRes,
        parentTasksRes,
        childTasksRes,
      ] = await Promise.all([
        axios.get(`${API_BASE}/trainingAdmin/site?site=${selectedSite}`),
        axios.get(`${API_BASE}/reporting/staff/new?site=${selectedSite}`),
        axios.get(`${API_BASE}/reporting/staff/old?site=${selectedSite}`),
        axios.get(`${API_BASE}/reporting/staff/logged?site=${selectedSite}`),
        axios.get(
          `${API_BASE}/reporting/tasks/completed/parent?site=${selectedSite}`,
        ),
        axios.get(
          `${API_BASE}/reporting/tasks/completed/child?site=${selectedSite}`,
        ),
      ]);

      const data = siteRes.data;
      setEmployeesData(data);

      const csa = [];
      const manager = [];
      const supervisor = [];
      for (let i = 0; i < data.length; i++) {
        if (data[i].role === "CSA") csa.push(data[i]);
        else if (data[i].role === "Supervisor") supervisor.push(data[i]);
        else if (data[i].role === "Manager") manager.push(data[i]);
      }
      setCsas(csa);
      setSupervisors(supervisor);
      setManagers(manager);
      setManagerAv(average(manager));
      setSuperAv(average(supervisor));
      setCsaAv(average(csa));
      setEmails(data.filter((d) => d.email).length);

      setNewStaff(newStaffRes.data);
      setOldStaff(oldStaffRes.data);
      setStaffLogged(staffLoggedRes.data);

      setParentTasks(parentTasksRes.data);
      setParentTasksAllLM(
        (parentTasksRes.data.lastMonthData || []).slice(0, 5),
      );
      setParentTasksAllTM(
        (parentTasksRes.data.thisMonthData || []).slice(0, 5),
      );

      setChildTasks(childTasksRes.data);
      setChildTasksAllLM((childTasksRes.data.lastMonthData || []).slice(0, 5));
      setChildTasksAllTM((childTasksRes.data.thisMonthData || []).slice(0, 5));

      setShowSites(true);
    } catch (err) {
      console.log(err);
      setSiteError("Couldn't load data for this site. Please try again.");
    } finally {
      setSiteLoading(false);
    }
  }, []);

  const removeStaff = async (name, role, staffSite) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove '${name}'?`,
    );
    if (!confirmed) return;

    setRemovingName(name);
    try {
      await axios.post(`${API_BASE}/trainingAdmin/remove`, {
        name,
        role,
        site: staffSite,
      });
      alert(`'${name}' Has Been Removed From The System.`);
      showSiteTraining(staffSite);
    } catch (err) {
      console.log(err);
      alert(`Something went wrong removing '${name}'. Please try again.`);
    } finally {
      setRemovingName(null);
    }
  };

  const loginRate =
    staffLogged && staffLogged.logged && employeesData.length > 0
      ? ((staffLogged.logged / employeesData.length) * 100).toFixed(0)
      : "0";

  const taskColumn = (heading, items, totalLabel, totalValue) => (
    <div className="statCard">
      <p className="statCard__header">{heading}</p>
      {items.length === 0 ? (
        <p className="statCard__row statCard__row--empty">No tasks recorded</p>
      ) : (
        items.map((d, i) => (
          <p className="statCard__row" key={`${heading}-${d.name}-${i}`}>
            {d.name} - {d.total}
          </p>
        ))
      )}
      {totalValue !== undefined && (
        <p className="statCard__total">
          {totalLabel} | {totalValue}
        </p>
      )}
    </div>
  );

  return (
    <main className="reports">
      <Header />

      <section className="reportsContent">
        {sitesLoading && <p className="reportsStatus">Loading sites…</p>}

        {!sitesLoading && sitesError && (
          <div className="reportsStatus">
            <p className="reportsError">{sitesError}</p>
            <button
              className="reportsRetryBtn"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {!sitesLoading && !sitesError && sites.length === 0 && (
          <p className="reportsStatus">No sites found for this account.</p>
        )}

        {!sitesLoading &&
          !sitesError &&
          sites.map((d, i) => (
            <div
              key={d.site || i}
              className={`siteReport${site === d.site && showSites ? " siteReport--active" : ""}`}
              onClick={() => showSiteTraining(d.site)}
            >
              <p>
                {d.site} <span>has completed</span>
                {d.percentage}%
              </p>
            </div>
          ))}
      </section>

      {siteLoading && <p className="reportsStatus">Loading {site}…</p>}

      {!siteLoading && siteError && (
        <div className="reportsStatus">
          <p className="reportsError">{siteError}</p>
          <button
            className="reportsRetryBtn"
            onClick={() => showSiteTraining(site)}
          >
            Retry
          </button>
        </div>
      )}

      {!siteLoading && !siteError && showSites && (
        <div className="employees">
          <div className="siteStats">
            <hr />
            <h1>{site}</h1>
            <h4>
              {employeesData.length} Employees are registered in this store
            </h4>
            <ul id="siteStatsList"> 
              <li>
                There is {managers.length} managers, with an average of{" "}
                {managerAv}%
              </li>
              <li>
                There is {supervisors.length} supervisors, with an average{" "}
                {superAv}%
              </li>
              <li>
                There is {csas.length} CSA, with an average of {csaAv}%
              </li>
              <li>
                There is {emails} emails recorded, on a total of{" "}
                {employeesData.length} employees registered
              </li>
              <li>Login Rate For The Staff Training Is {loginRate}%</li>
            </ul>
          </div>
          <hr />

          <div className="statsRow">
            {taskColumn(
              "Top 5 Tasks Last Month",
              parentTasksAllLM,
              "Total Last Month",
              parentTasks && parentTasks.lastMonth
                ? parentTasks.lastMonth.length
                : undefined,
            )}

            {taskColumn(
              "Top 5 Tasks This Month",
              parentTasksAllTM,
              "Total This Month",
              parentTasks && parentTasks.thisMonth
                ? parentTasks.thisMonth.length
                : undefined,
            )}

            {taskColumn(
              "Top 5 Sub-Tasks Last Month",
              childTasksAllLM,
              "Total Last Month",
              childTasks && childTasks.lastMonth
                ? childTasks.lastMonth.length
                : undefined,
            )}

            {taskColumn(
              "Top 5 Sub-Tasks This Month",
              childTasksAllTM,
              "Total This Month",
              childTasks && childTasks.thisMonth
                ? childTasks.thisMonth.length
                : undefined,
            )}

            <div className="statCard">
              <p className="statCard__header">Staff Stats</p>

              {newStaff && newStaff.thisMonth && (
                <>
                  <p className="statCard__row">
                    New This Month - {newStaff.thisMonth.length}
                  </p>
                  <p className="statCard__row">
                    New Last Month - {newStaff.lastMonth.length}
                  </p>
                </>
              )}

              {oldStaff && oldStaff.thisMonth && (
                <>
                  <p className="statCard__row">
                    Removed This Month - {oldStaff.thisMonth.length}
                  </p>
                  <p className="statCard__row">
                    Removed Last Month - {oldStaff.lastMonth.length}
                  </p>
                </>
              )}

              {staffLogged && staffLogged.logged !== undefined && (
                <p className="statCard__total">
                  Staff Logged Into System | {staffLogged.logged}
                </p>
              )}
            </div>
          </div>

          {employeesData.length === 0 ? (
            <p className="reportsStatus">
              No employees registered at this site yet.
            </p>
          ) : (
            employeesData.map((d, i) => {
              const hs = d.area1 ? (
                <DoneIcon fontSize="medium" className="tick" />
              ) : (
                <CloseIcon fontSize="medium" className="cross" />
              );
              const hot = d.area2 ? (
                <DoneIcon fontSize="medium" className="tick" />
              ) : (
                <CloseIcon fontSize="medium" className="cross" />
              );
              const ms = d.area3 ? (
                <DoneIcon fontSize="medium" className="tick" />
              ) : (
                <CloseIcon fontSize="medium" className="cross" />
              );
              const fa = d.area4 ? (
                <DoneIcon fontSize="medium" className="tick" />
              ) : (
                <CloseIcon fontSize="medium" className="cross" />
              );
              const isRemoving = removingName === d.name;

              return (
                <div className="employee" key={`${d.name}-${i}`}>
                  <div className="mainProgress">
                    <CircularProgressbar
                      value={d.percent}
                      text={`${d.percent}%`}
                    />
                  </div>
                  <p>
                    <span
                      style={{ color: "#1d1d1d", textTransform: "uppercase" }}
                    >
                      {d.name}
                    </span>
                    <span className="site">{d.site}</span>
                    <div className="shown">
                      <span>H&amp;S</span>
                      <span>{hs}</span>
                    </div>
                    <div className="Notshown">
                      <span>Floor</span>
                      <span>{hot}</span>
                    </div>
                    <div className="shown">
                      <span>Alcohol</span>
                      <span>{ms}</span>
                    </div>
                    <div className="shown">
                      <span>Tills</span>
                      <span>{fa}</span>
                    </div>
                  </p>
                  <div className="actions">
                    <Tooltip title="Remove Staff">
                      <span>
                        <button
                          disabled={isRemoving}
                          onClick={() => removeStaff(d.name, d.role, d.site)}
                        >
                          <PersonRemoveIcon />
                        </button>
                      </span>
                    </Tooltip>
                    <Tooltip title="View Employee">
                      <button>
                        <a
                          href={`/employee?name=${d.name}&role=${d.role}&site=${d.site}`}
                        >
                          <VisibilityIcon />
                        </a>
                      </button>
                    </Tooltip>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </main>
  );
}
